import {
  View,
  Text,
  ScrollView,
  Pressable,
  ActivityIndicator,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useColors } from "../lib/colors";
import { webPrivacyProps } from "../lib/privacy";
import { ResponsiveModal } from "./ResponsiveModal";
import {
  fmtAmt,
  SectionDivider,
  LineItem,
  Subtotal,
} from "./cashflow/shared";
import type { CashFlowModalProps } from "./cashflow/types";

export function CashFlowModal({
  visible,
  onClose,
  data,
  isLoading,
  onTaxDashboardPress,
  emptyMessage,
}: CashFlowModalProps) {
  const c = useColors();

  return (
    <ResponsiveModal
      visible={visible}
      onClose={onClose}
      header={(
        <View className="flex-row items-center gap-2">
          <Text className="text-xl font-bold text-gray-900 dark:text-white">
            {data?.year ?? new Date().getFullYear()} Cash Flow
          </Text>
          {data?.age != null && (
            <View className="rounded-full px-2 py-0.5 bg-gray-100 dark:bg-gray-700">
              <Text
                className="text-xs font-medium text-gray-600 dark:text-gray-300"
                {...webPrivacyProps('private')}
              >
                Age {data.age}
                {data.agePersonName ? ` (${data.agePersonName})` : ""}
              </Text>
            </View>
          )}
          {data?.isFiYear && (
            <View className="rounded-full px-2 py-0.5 bg-green-100 dark:bg-green-900/40">
              <Text className="text-xs font-semibold text-green-700 dark:text-green-400">
                FI Year
              </Text>
            </View>
          )}
        </View>
      )}
      subtitle={data?.subtitle}
      maxHeight={760}
      androidMinHeight={360}
    >
      {(data?.retirementNotices?.length ?? 0) > 0 && (
        <View className="mx-5 mb-2 rounded-xl bg-green-50 dark:bg-green-900/30 px-4 py-3">
          {data!.retirementNotices!.map((notice, i) => (
            <Text
              key={i}
              className="text-xs text-green-700 dark:text-green-400"
            >
              {notice}
            </Text>
          ))}
        </View>
      )}

      {isLoading ? (
        <View className="py-16 items-center">
          <ActivityIndicator color={c.primary} />
          <Text className="text-sm text-gray-400 mt-3">
            Loading cash flow...
          </Text>
        </View>
      ) : !data ? (
        <View className="py-16 items-center">
          <Text className="text-sm text-gray-400 dark:text-gray-500">
            {emptyMessage ?? "No data available yet."}
          </Text>
        </View>
      ) : (
        <>
          <ScrollView
            className="flex-1 px-5"
            showsVerticalScrollIndicator={false}
          >
            <>
                {/* ── Income ── */}
                <SectionDivider title="Income" />
                {data.incomeGroups.length === 0 ? (
                  <Text className="text-sm text-gray-400 dark:text-gray-500 italic py-1">
                    No income streams active this year
                  </Text>
                ) : (
                  data.incomeGroups.map((group, gi) => (
                    <View key={gi}>
                      {group.heading && (
                        <Text className="text-xs text-gray-400 dark:text-gray-500 mt-1 mb-0.5">
                          {group.heading}
                        </Text>
                      )}
                      {group.items.map((item, ii) => (
                        <LineItem
                          key={ii}
                          label={item.label}
                          amount={item.amount}
                          amountColor="text-green-600 dark:text-green-500"
                        />
                      ))}
                    </View>
                  ))
                )}
                <Subtotal label={data.incomeTotalLabel} amount={data.incomeTotal} />

                {/* ── Expenses ── */}
                <SectionDivider title="Expenses" />
                {data.expenseItems.length === 0 ? (
                  <Text className="text-sm text-gray-400 dark:text-gray-500 italic py-1">
                    No expense data yet
                  </Text>
                ) : (
                  data.expenseItems.map((item, i) => (
                    <LineItem
                      key={i}
                      label={item.label}
                      amount={item.amount}
                      note={item.note}
                    />
                  ))
                )}
                <Subtotal label="Total Expenses" amount={data.expenseTotal} />

                {/* ── Taxes ── */}
                {data.taxes && (
                  <>
                    <SectionDivider title="Taxes" />
                    {data.taxes.type === "detailed" ? (
                      <>
                        {data.taxes.estimatedTaxOwed !== null ? (
                          <LineItem
                            label="Estimated Tax Owed / Refund"
                            amount={data.taxes.estimatedTaxOwed}
                          />
                        ) : onTaxDashboardPress ? (
                          <Pressable
                            onPress={onTaxDashboardPress}
                            className="flex-row items-center justify-between py-2"
                          >
                            <View className="flex-1">
                              <Text className="text-sm text-gray-700 dark:text-gray-300">
                                Estimated Tax Owed / Refund
                              </Text>
                              <Text className="text-xs text-gray-400 dark:text-gray-500">
                                Coming soon
                              </Text>
                            </View>
                            <View className="flex-row items-center gap-1">
                              <Text className="text-xs font-medium text-green-600 dark:text-green-500">
                                View tax dashboard
                              </Text>
                              <Ionicons
                                name="chevron-forward"
                                size={14}
                                color={c.primary}
                              />
                            </View>
                          </Pressable>
                        ) : null}
                        {data.taxes.withholding && (
                          <>
                            <View className="mt-2">
                              <Text className="text-xs text-gray-400 dark:text-gray-500 mb-1">
                                Annual Withholding (from payroll)
                              </Text>
                            </View>
                            {data.taxes.withholding.federal > 0 && (
                              <LineItem
                                label="Federal"
                                amount={data.taxes.withholding.federal}
                              />
                            )}
                            {data.taxes.withholding.state > 0 && (
                              <LineItem
                                label="State"
                                amount={data.taxes.withholding.state}
                              />
                            )}
                            {data.taxes.withholding.fica > 0 && (
                              <LineItem
                                label="FICA (SS + Medicare)"
                                amount={data.taxes.withholding.fica}
                              />
                            )}
                            <Subtotal
                              label="Total Withheld"
                              amount={
                                data.taxes.withholding.federal +
                                data.taxes.withholding.state +
                                data.taxes.withholding.fica
                              }
                            />
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {data.taxes.federal > 0 && (
                          <LineItem label="Federal" amount={data.taxes.federal} />
                        )}
                        {data.taxes.state > 0 && (
                          <LineItem label="State" amount={data.taxes.state} />
                        )}
                        <Subtotal label="Total Tax" amount={data.taxes.total} />
                      </>
                    )}
                  </>
                )}

                {/* ── Retirement Savings ── */}
                {data.retirementSavings && data.retirementSavingsTotal != null && data.retirementSavingsTotal > 0 && (
                  <>
                    <SectionDivider title="Retirement Savings" />
                    {data.retirementSavings.map((item, i) => (
                      <LineItem
                        key={i}
                        label={item.label}
                        amount={item.amount}
                        amountColor="text-green-600 dark:text-green-500"
                        note={item.note}
                      />
                    ))}
                    <Subtotal
                      label="Total Contributions"
                      amount={data.retirementSavingsTotal}
                    />
                  </>
                )}

                {/* ── Life Events ── */}
                {data.lifeEvents && (
                  <>
                    <SectionDivider title="Life Events" />
                    {data.lifeEvents.length === 0 ? (
                      <Text className="text-sm text-gray-400 dark:text-gray-500 italic py-1">
                        No life events this year
                      </Text>
                    ) : (
                      <>
                        {data.lifeEvents
                          .filter((e) => e.type === "inheritance")
                          .map((e, i) => (
                            <LineItem
                              key={`i${i}`}
                              label={e.description}
                              amount={e.amount}
                              amountColor="text-green-600 dark:text-green-500"
                              note="Windfall / Liquidity Event"
                            />
                          ))}
                        {data.lifeEvents
                          .filter((e) => e.type === "asset_sale")
                          .map((e, i) => (
                            <LineItem
                              key={`a${i}`}
                              label={e.description}
                              amount={e.amount}
                              amountColor="text-green-600 dark:text-green-500"
                              note="Windfall / Liquidity Event"
                            />
                          ))}
                        {data.lifeEvents
                          .filter((e) => e.type === "expense")
                          .map((e, i) => (
                            <LineItem
                              key={`e${i}`}
                              label={e.description}
                              amount={e.amount}
                              amountColor="text-red-500 dark:text-red-400"
                              note="Expense"
                            />
                          ))}
                        {(() => {
                          const inflows = data.lifeEvents!
                            .filter((e) => e.type !== "expense")
                            .reduce((s, e) => s + e.amount, 0);
                          const expenses = data.lifeEvents!
                            .filter((e) => e.type === "expense")
                            .reduce((s, e) => s + e.amount, 0);
                          const net = inflows - expenses;
                          return (
                            <Subtotal
                              label={net >= 0 ? "Net Inflow" : "Net Outflow"}
                              amount={Math.abs(net)}
                              color={
                                net >= 0
                                  ? "text-green-600 dark:text-green-500"
                                  : "text-red-500 dark:text-red-400"
                              }
                            />
                          );
                        })()}
                      </>
                    )}
                  </>
                )}

                {/* ── Net Cash Flow ── */}
                <SectionDivider title="Net Cash Flow" />
                <View className="flex-row items-center justify-between py-1">
                  <Text className="text-sm text-gray-500 dark:text-gray-400">
                    Income
                  </Text>
                  <Text className="text-sm text-green-600 dark:text-green-500">
                    +{fmtAmt(data.cashFlowBridge.incomeAmount)}
                  </Text>
                </View>
                <View className="flex-row items-center justify-between py-1">
                  <Text className="text-sm text-gray-500 dark:text-gray-400">
                    Expenses
                  </Text>
                  <Text className="text-sm text-gray-700 dark:text-gray-300">
                    -{fmtAmt(data.cashFlowBridge.expenseAmount)}
                  </Text>
                </View>
                {data.cashFlowBridge.taxAmount != null &&
                  data.cashFlowBridge.taxAmount > 0 && (
                    <View className="flex-row items-center justify-between py-1">
                      <Text className="text-sm text-gray-500 dark:text-gray-400">
                        Taxes
                      </Text>
                      <Text className="text-sm text-gray-700 dark:text-gray-300">
                        -{fmtAmt(data.cashFlowBridge.taxAmount)}
                      </Text>
                    </View>
                  )}
                {data.cashFlowBridge.lifeEventAmount != null &&
                  data.cashFlowBridge.lifeEventAmount !== 0 && (
                    <View className="flex-row items-center justify-between py-1">
                      <Text className="text-sm text-gray-500 dark:text-gray-400">
                        Life Events
                      </Text>
                      <Text
                        className={`text-sm ${
                          data.cashFlowBridge.lifeEventAmount >= 0
                            ? "text-green-600 dark:text-green-500"
                            : "text-gray-700 dark:text-gray-300"
                        }`}
                      >
                        {data.cashFlowBridge.lifeEventAmount >= 0 ? "+" : "-"}
                        {fmtAmt(Math.abs(data.cashFlowBridge.lifeEventAmount))}
                      </Text>
                    </View>
                  )}
                <View className="flex-row items-center justify-between pt-2 mt-1 border-t border-gray-100 dark:border-gray-700">
                  <Text className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {data.cashFlowBridge.netLabel}
                  </Text>
                  <Text
                    className={`text-sm font-semibold ${
                      data.cashFlowBridge.netAmount >= 0
                        ? "text-green-600 dark:text-green-500"
                        : "text-red-500 dark:text-red-400"
                    }`}
                  >
                    {data.cashFlowBridge.netAmount >= 0 ? "+" : "-"}
                    {fmtAmt(Math.abs(data.cashFlowBridge.netAmount))}
                  </Text>
                </View>

                {data.scheduleNote && (
                  <Text className="text-xs text-gray-500 dark:text-gray-400 mt-3">{data.scheduleNote}</Text>
                )}
                {/* ── Contribution Schedule ── */}
                {data.contributionSchedule && data.contributionSchedule.length > 0 && (
                  <>
                    <SectionDivider title="Contribution Schedule" />
                    {data.contributionSchedule.map((item, i) => (
                      <LineItem
                        key={i}
                        label={item.label}
                        amount={item.amount}
                        amountColor="text-green-600 dark:text-green-500"
                      />
                    ))}
                    {data.contributionTotal != null && (
                      <Subtotal
                        label="Total Contributions"
                        amount={data.contributionTotal}
                        color="text-green-600 dark:text-green-500"
                      />
                    )}
                  </>
                )}

                {/* ── Draw Schedule ── */}
                {data.drawSchedule && data.drawSchedule.length > 0 && (
                  <>
                    <SectionDivider title="Draw Schedule" />
                    {data.drawSchedule.map((item, i) => (
                      <LineItem
                        key={i}
                        label={item.label}
                        amount={item.amount}
                        amountColor="text-red-500 dark:text-red-400"
                      />
                    ))}
                    {data.drawTotal != null && (
                      <Subtotal
                        label="Total Draws"
                        amount={data.drawTotal}
                        color="text-red-500 dark:text-red-400"
                      />
                    )}
                  </>
                )}

                <View className="h-8" />
            </>
          </ScrollView>

          {/* Prev / Next navigation */}
          {data.navigation && (
            <View className="flex-row items-center justify-between px-5 py-4 border-t border-gray-100 dark:border-gray-700">
              <Pressable
                onPress={() =>
                  data.navigation!.prevYear != null
                    ? data.navigation!.onNavigate(data.navigation!.prevYear)
                    : undefined
                }
                disabled={data.navigation.prevYear == null}
                className={`flex-row items-center gap-1 px-3 py-2 rounded-lg ${
                  data.navigation.prevYear != null
                    ? "bg-gray-100 dark:bg-gray-700"
                    : "opacity-30"
                }`}
              >
                <Ionicons
                  name="chevron-back"
                  size={16}
                  color={c.textSecondary}
                />
                <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {data.navigation.prevYear ?? "—"}
                </Text>
              </Pressable>

              <Text className="text-xs text-gray-400 dark:text-gray-500">
                Tap a row to jump to any year
              </Text>

              <Pressable
                onPress={() =>
                  data.navigation!.nextYear != null
                    ? data.navigation!.onNavigate(data.navigation!.nextYear)
                    : undefined
                }
                disabled={data.navigation.nextYear == null}
                className={`flex-row items-center gap-1 px-3 py-2 rounded-lg ${
                  data.navigation.nextYear != null
                    ? "bg-gray-100 dark:bg-gray-700"
                    : "opacity-30"
                }`}
              >
                <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {data.navigation.nextYear ?? "—"}
                </Text>
                <Ionicons
                  name="chevron-forward"
                  size={16}
                  color={c.textSecondary}
                />
              </Pressable>
            </View>
          )}
        </>
      )}
    </ResponsiveModal>
  );
}
